import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail, downloadExport } from "../lib/api";
import { PageHeader, EmptyState, StatusPill, Confirm } from "../components/Shared";
import { fmtFCFA, fmtDate, ROLES } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash2, FileText, Smartphone, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { TableSkeleton } from "../components/Skeletons";

const PROVIDERS = ["MTN MoMo", "Orange Money"];
const STATUSES = [["completed", "Completed"], ["pending", "Pending"], ["failed", "Failed"]];
const SOURCES = ["Salary", "Allowance"];
const empty = { employee_id: "", employee_name: "", source_type: "Salary", source_label: "", allowance_key: "", provider: "MTN MoMo", phone: "", payer_phone: "", amount: "", reference: "", status: "completed", note: "", paid_at: "" };
const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-sm">{label}</Label>{children}</div>;
const salAmount = (s) => (Number(s?.salary) || Number(s?.contract_amount) || 0);

export default function MobileMoney() {
  const { isRole } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const canRecord = isRole(ROLES.FM);
  const canDelete = isRole(ROLES.FM);

  const { data: rows = [], isLoading } = useQuery({ queryKey: ["momo-payments"], queryFn: () => api.get("/momo-payments").then((r) => r.data) });
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: () => api.get("/employees").then((r) => r.data) });
  const { data: salaries = [] } = useQuery({ queryKey: ["salaries"], queryFn: () => api.get("/salaries").then((r) => r.data) });
  const { data: senders = [] } = useQuery({ queryKey: ["momo-senders"], queryFn: () => api.get("/momo-senders").then((r) => r.data) });
  const [newSender, setNewSender] = useState("");
  const addSender = async () => {
    const v = newSender.trim();
    if (!v) return;
    try {
      const { data } = await api.post("/momo-senders", { name: v });
      toast.success("Paying number added");
      setNewSender("");
      setForm((f) => ({ ...f, payer_phone: data.name }));
      qc.invalidateQueries({ queryKey: ["momo-senders"] });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const empSalary = (id) => salaries.find((s) => s.employee_id === id) || null;
  const empAllowances = (id) => (empSalary(id)?.allowances || []).filter((a) => (a.amount || 0) > 0);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("prefill") === "1") {
      setForm({
        ...empty,
        employee_id: searchParams.get("employee_id") || "",
        employee_name: searchParams.get("name") || "",
        phone: searchParams.get("phone") || "",
        amount: searchParams.get("amount") || "",
        source_type: SOURCES.includes(searchParams.get("source_type")) ? searchParams.get("source_type") : "Salary",
        source_label: searchParams.get("source_label") || "",
        provider: PROVIDERS.includes(searchParams.get("provider")) ? searchParams.get("provider") : "MTN MoMo",
      });
      setOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setForm(empty); setOpen(true); };

  const pickEmployee = (id) => {
    const emp = employees.find((e) => e.id === id);
    const sal = empSalary(id);
    setForm((f) => ({
      ...f, employee_id: id, employee_name: emp?.name || "", phone: emp?.phone || f.phone,
      amount: f.source_type === "Salary" && sal ? String(Math.round(salAmount(sal))) : f.amount,
    }));
  };
  const pickAllowance = (key) => {
    const a = empAllowances(form.employee_id).find((x) => x.key === key);
    setForm((f) => ({ ...f, allowance_key: key, source_label: a ? `${labelFor(a.key)} allowance` : "", amount: a ? String(Math.round(a.amount)) : f.amount }));
  };

  const save = async () => {
    if (!form.employee_name) { toast.error("Select an employee."); return; }
    if (!(Number(form.amount) > 0)) { toast.error("Enter an amount greater than zero."); return; }
    if (!form.phone.trim()) { toast.error("Enter the recipient mobile money number."); return; }
    setSaving(true);
    try {
      await api.post("/momo-payments", {
        employee_id: form.employee_id, employee_name: form.employee_name,
        source_type: form.source_type, source_label: form.source_label.trim(),
        provider: form.provider, phone: form.phone.trim(), payer_phone: form.payer_phone.trim(), amount: Number(form.amount),
        reference: form.reference.trim(), status: form.status, note: form.note.trim(),
        paid_at: form.paid_at || "",
      });
      toast.success("Mobile money payment recorded");
      setOpen(false); qc.invalidateQueries();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  const sendLive = async () => {
    if (!form.employee_name) { toast.error("Select an employee."); return; }
    if (!(Number(form.amount) > 0)) { toast.error("Enter an amount greater than zero."); return; }
    if (!form.phone.trim()) { toast.error("Enter the recipient MTN number."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/momo-payments/send", {
        employee_id: form.employee_id, employee_name: form.employee_name,
        source_type: form.source_type, source_label: form.source_label.trim(),
        provider: "MTN MoMo", phone: form.phone.trim(), payer_phone: form.payer_phone.trim(), amount: Number(form.amount),
        reference: form.reference.trim(), note: form.note.trim(),
      });
      toast.success(`Sent via MTN — status: ${data.status}`);
      setOpen(false); qc.invalidateQueries();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSending(false);
  };

  const refreshStatus = async (p) => {
    try { const { data } = await api.get(`/momo-payments/${p.id}/status`); toast.info(`MTN status: ${data.status}`); qc.invalidateQueries(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (p) => {
    try { await api.delete(`/momo-payments/${p.id}`); toast.success("Payment deleted"); qc.invalidateQueries(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const total = rows.filter((r) => r.status === "completed").reduce((s, r) => s + (Number(r.amount) || 0), 0);

  return (
    <div>
      <PageHeader title="Mobile Money" subtitle="Record salary & allowance payments made via MTN MoMo and Orange Money">
        {canRecord && <Button data-testid="momo-add-button" onClick={openNew} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Plus className="h-4 w-4 mr-2" /> Record Payment</Button>}
      </PageHeader>

      {rows.length > 0 && (
        <div className="mb-4 rounded-xl border border-border bg-card shadow-sm p-4" data-testid="momo-total">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Total paid via mobile money (completed)</div>
          <div className="text-2xl font-bold text-foreground font-mono-num mt-1" data-testid="momo-total-value">{fmtFCFA(total)}</div>
        </div>
      )}

      <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
        {isLoading ? <TableSkeleton cols={7} /> : rows.length === 0 ? (
          <EmptyState icon={Smartphone} title="No mobile money payments yet" description="Record a salary or allowance paid via MTN MoMo or Orange Money to keep a full audit trail." />
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Employee</TableHead><TableHead>For</TableHead><TableHead>Provider</TableHead>
              <TableHead>Number</TableHead><TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead><TableHead>Paid</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((p) => (
                <TableRow key={p.id} data-testid={`momo-row-${p.id}`}>
                  <TableCell className="font-medium text-foreground">{p.employee_name}</TableCell>
                  <TableCell className="text-muted-foreground">{p.source_label || p.source_type}</TableCell>
                  <TableCell><ProviderChip provider={p.provider} /></TableCell>
                  <TableCell className="font-mono-num text-muted-foreground">{p.phone}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtFCFA(p.amount)}</TableCell>
                  <TableCell><StatusPill status={p.status} testId={`momo-status-${p.id}`} /></TableCell>
                  <TableCell className="text-muted-foreground text-sm">{fmtDate(p.paid_at || p.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="stems-actions">
                      {p.status === "pending" && p.momo_ref && <Button size="icon" variant="ghost" data-testid={`momo-refresh-${p.id}`} title="Check MTN status" onClick={() => refreshStatus(p)}><RefreshCw className="h-4 w-4" /></Button>}
                      <Button size="icon" variant="ghost" data-testid={`momo-receipt-${p.id}`} title="Download receipt" onClick={() => downloadExport(`/export/momo/${p.id}/receipt.pdf`, `momo_receipt_${(p.id || "").slice(0, 8)}.pdf`)}><FileText className="h-4 w-4" /></Button>
                      {canDelete && <Confirm onConfirm={() => remove(p)} title="Delete payment?" description={`Remove the mobile money payment record for ${p.employee_name}.`}
                        trigger={<Button size="icon" variant="ghost" data-testid={`momo-delete-${p.id}`} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>} />}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="momo-dialog">
          <DialogHeader><DialogTitle>Make Mobile Money Payment</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F label="Payment for">
              <Select value={form.source_type} onValueChange={(v) => setForm((f) => ({ ...f, source_type: v, allowance_key: "", source_label: v === "Salary" ? "" : f.source_label }))}>
                <SelectTrigger data-testid="momo-source-type"><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Employee">
              <Select value={form.employee_id} onValueChange={pickEmployee}>
                <SelectTrigger data-testid="momo-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            {form.source_type === "Allowance" && form.employee_id && (
              <div className="sm:col-span-2"><F label="Allowance">
                {empAllowances(form.employee_id).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No funded allowances on this employee — enter a label & amount below.</p>
                ) : (
                  <Select value={form.allowance_key} onValueChange={pickAllowance}>
                    <SelectTrigger data-testid="momo-allowance"><SelectValue placeholder="Select allowance" /></SelectTrigger>
                    <SelectContent>{empAllowances(form.employee_id).map((a) => <SelectItem key={a.key} value={a.key}>{labelFor(a.key)} · {fmtFCFA(a.amount)}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </F></div>
            )}
            {form.source_type === "Allowance" && (
              <div className="sm:col-span-2"><F label="Label (what is being paid)"><Input data-testid="momo-label" value={form.source_label} onChange={(e) => setForm({ ...form, source_label: e.target.value })} placeholder="e.g. Transport allowance" /></F></div>
            )}
            <F label="Provider">
              <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                <SelectTrigger data-testid="momo-provider"><SelectValue /></SelectTrigger>
                <SelectContent>{PROVIDERS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <div className="space-y-1.5">
              <Label className="text-sm">Number making the payment</Label>
              <Select value={form.payer_phone} onValueChange={(v) => setForm({ ...form, payer_phone: v })}>
                <SelectTrigger data-testid="momo-payer-phone"><SelectValue placeholder="Select paying number" /></SelectTrigger>
                <SelectContent>
                  {senders.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No numbers saved yet — add one below</div>}
                  {senders.map((s) => <SelectItem key={s.id} value={s.name} data-testid={`momo-payer-opt-${s.id}`}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex gap-1.5">
                <Input data-testid="momo-new-sender" value={newSender} onChange={(e) => setNewSender(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSender(); } }} placeholder="Add a paying number" className="h-8 text-xs" />
                <Button type="button" size="sm" variant="outline" data-testid="momo-add-sender" onClick={addSender} className="h-8 shrink-0">Add</Button>
              </div>
            </div>
            <F label="Recipient number"><Input data-testid="momo-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+2376XXXXXXXX" /></F>
            <F label="Amount (FCFA)"><Input data-testid="momo-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" /></F>
            <F label="Transaction reference"><Input data-testid="momo-reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="e.g. MP2406.1234.567" /></F>
            <F label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="momo-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Payment date"><Input data-testid="momo-paid-at" type="date" value={(form.paid_at || "").slice(0, 10)} onChange={(e) => setForm({ ...form, paid_at: e.target.value })} /></F>
            <div className="sm:col-span-2"><F label="Note"><Textarea data-testid="momo-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Optional" /></F></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            {form.provider === "MTN MoMo" ? (
              <>
                <Button variant="outline" data-testid="momo-submit" onClick={save} disabled={saving || sending || !form.employee_name}>Record only</Button>
                <Button data-testid="momo-send" onClick={sendLive} disabled={sending || saving || !form.employee_name} className="bg-[#FFCC00] hover:bg-[#e6b800] text-black font-semibold"><Smartphone className="h-4 w-4 mr-2" />{sending ? "Sending…" : "Send via MTN"}</Button>
              </>
            ) : (
              <Button data-testid="momo-submit" onClick={save} disabled={saving || !form.employee_name} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">Make Momo Payment</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ALLOWANCE_LABELS = { duty_post: "Duty post", telephone: "Telephone", transport: "Transport", food: "Food", encouragement: "Encouragement" };
function labelFor(key) { return ALLOWANCE_LABELS[key] || key; }

function ProviderChip({ provider }) {
  const mtn = (provider || "").toLowerCase().includes("mtn");
  const cls = mtn ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-orange-100 text-orange-800 border-orange-200";
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cls}`}><Smartphone className="h-3 w-3" />{provider}</span>;
}
